//-- Copyright (c) 2021 TMT Insights Group, LLC
import PolarisIcon from '@/components/_common/PolarisIcon';
import { selectHasUnseenNotifications } from '@/redux/selectors/notificationSelectors';
import styled from '@emotion/styled';
import { ClickAwayListener, IconButton, Portal } from '@mui/material';
import React, { useState, useRef, useEffect } from 'react';
import NotificationPopoutContent from './NotificationPopoutContent';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { getNotifications } from '@/redux/slices/notificationSlice';

interface NotificationsPopoutProps {
  onShowModal: () => void;
  onShowNotificationSettings: () => void;
}

const NotificationsPopout = ({ onShowModal, onShowNotificationSettings }: NotificationsPopoutProps) => {
  const dispatch = useAppDispatch();
  const hasUnseenNotifications = useAppSelector(selectHasUnseenNotifications);

  // the popover should be positioned relative to the icon button that is opening it, so it cannot be positioned sticky to the scrolling window
  // however we need to set it's container to document.body for it not hide behined sticky positioned table columns - so we need to control it with getBoundingClientRect
  const anchorRef = useRef<HTMLButtonElement | null>(null);

  const [showPopup, setShowPopup] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });

  const updatePosition = () => {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;

    setCoords({
      top: rect.bottom + 8,
      left: rect.right - 370
    });
  };

  const onShowPopout = () => {
    if (showPopup) {
      setShowPopup(false);
      return;
    }

    dispatch(getNotifications(0));

    updatePosition();
    setShowPopup(true);
  };

  const onHidePopout = () => {
    setShowPopup(false);
  };

  const onViewAllClicked = () => {
    onHidePopout();
    onShowModal();
  };

  useEffect(() => {
    if (!showPopup) return;

    updatePosition();

    window.addEventListener('scroll', updatePosition);
    window.addEventListener('resize', updatePosition);

    return () => {
      window.removeEventListener('scroll', updatePosition);
      window.removeEventListener('resize', updatePosition);
    };
  }, [showPopup]);

  return (
    <ClickAwayListener onClickAway={onHidePopout}>
      <RelativeWrapper>
        <IconButton id="notif-toggle" onClick={onShowPopout} size="large" ref={anchorRef}>
          <PolarisIcon icon="notifications" color="white" />
          {hasUnseenNotifications && <RedCircle />}
        </IconButton>
        {showPopup && (
          <Portal container={document.body}>
            <PortalWrapper style={{ top: coords.top, left: coords.left }}>
              <Arrow />
              <NotificationPopoutContent
                onShowNotificationSettings={onShowNotificationSettings}
                onViewAllClicked={onViewAllClicked}
                onCloseClicked={onHidePopout}
              />
            </PortalWrapper>
          </Portal>
        )}
      </RelativeWrapper>
    </ClickAwayListener>
  );
};

export default NotificationsPopout;

const RelativeWrapper = styled.div`
  display: inline-block;
  position: relative;
  margin-top: ${(props) => props.theme.spacing(-1)};
  margin-bottom: ${(props) => props.theme.spacing(-1)};
  margin-right: ${(props) => props.theme.spacing(1.5)};
`;

const PortalWrapper = styled.div`
  position: fixed;
  z-index: 999999;
`;

const Arrow = styled.div`
  position: absolute;
  right: 75px;
  top: -10px;
  width: 20px;
  height: 20px;
  transform: rotate(45deg);
  background-color: ${({ theme }) => theme.palette.background.paper};
`;

const RedCircle = styled.div`
  background-color: ${({ theme }) => theme.palette.error.main};
  width: 10px;
  height: 10px;
  border-radius: 10px;
  position: absolute;
  margin-right: -14px;
  margin-top: -12px;
`;
